import { Section } from '@components/index'
import { SectionTitle } from '@components/section/SectionTitle'
import type {
  MessageAnalytics,
  UnreadMessageUser
} from '@services/dashboard/queries/useMessageAnalytics'
import { format } from 'date-fns'
import { ChevronLeft, ChevronRight, MessageSquare } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { cn } from '@/lib/utils'

interface MessageAnalyticsTableProps {
  analytics?: MessageAnalytics
  loading?: boolean
}

const PAGE_SIZE = 10
const TH =
  'px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground'
const TD = 'px-4 py-3.5 align-middle text-sm text-ink'

function UnreadBadge({ count }: { count: number }) {
  return (
    <span className="inline-flex min-w-6 items-center justify-center rounded-full bg-peach/30 px-2 py-0.5 text-xs font-bold text-warning">
      {count}
    </span>
  )
}

export function MessageAnalyticsTable({
  analytics,
  loading = false
}: MessageAnalyticsTableProps) {
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const [page, setPage] = useState(0)

  const users = analytics?.users_with_unread ?? []
  const pages = Math.ceil(users.length / PAGE_SIZE)
  const rows = users.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)

  return (
    <Section
      title={
        <span className="flex w-full items-center gap-3">
          <SectionTitle
            icon={<MessageSquare />}
            title={t('messageTable.title')}
          />
          <UnreadBadge count={analytics?.total_unread ?? 0} />
        </span>
      }
      defaultActiveKey={['0']}
    >
      {loading ? (
        <div className="h-24 animate-pulse rounded-xl bg-ink/[0.04]" />
      ) : users.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {t('messageTable.empty', { defaultValue: '—' })}
        </p>
      ) : (
        <div className="-mx-5 -mb-5">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-y border-ink/[0.08] bg-ink/[0.02] text-left">
                  <th className={TH}>{t('messageTable.user')}</th>
                  <th className={cn(TH, 'text-center')}>
                    {t('messageTable.unreadCount')}
                  </th>
                  <th className={TH}>{t('messageTable.lastMessage')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u: UnreadMessageUser) => (
                  <tr
                    key={u.user_id}
                    onClick={() => navigate(`/admin/users/${u.user_id}`)}
                    className="cursor-pointer border-b border-ink/[0.05] transition-colors last:border-0 hover:bg-ink/[0.015]"
                  >
                    <td className={TD}>
                      <div className="font-medium text-ink">
                        {u.user_name ?? u.user_email}
                      </div>
                      {u.user_name && (
                        <div className="text-xs text-muted-foreground">
                          {u.user_email}
                        </div>
                      )}
                    </td>
                    <td className={cn(TD, 'text-center')}>
                      <UnreadBadge count={u.unread_count} />
                    </td>
                    <td className={cn(TD, 'whitespace-nowrap')}>
                      {u.last_message_date
                        ? format(
                            new Date(u.last_message_date),
                            'dd.MM.yyyy HH:mm'
                          )
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-end gap-1.5 px-4 py-3">
              <button
                type="button"
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink transition-colors hover:bg-ink/[0.05] disabled:opacity-30"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-2 text-sm font-medium text-muted-foreground">
                {page + 1} / {pages}
              </span>
              <button
                type="button"
                disabled={page === pages - 1}
                onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}
                className="flex h-8 w-8 items-center justify-center rounded-full border-0 bg-transparent p-0 text-ink transition-colors hover:bg-ink/[0.05] disabled:opacity-30"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </Section>
  )
}
